import { FC, useCallback, useMemo, useState } from "react";
import {Button, Flex, Group, Modal, Text, Tooltip} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useRouter } from "next/router";
import {
    IconEdit,
} from "@tabler/icons";
import {IconAlertCircle, IconBrandWhatsapp, IconChessKing, IconCircleX, IconClock, IconCopy, IconPlus, IconSend, IconUserOff, IconUserPlus, IconUserStar} from "@tabler/icons-react";
import Link from "next/link";
import Fetch from "@/utils/Fetch";
import Helper from "@/utils/helper";
import PS from "@/services/permission/permission-service";
import * as Enums from "@/utils/enums";
import Storage from "@/utils/storage";
import {ScTableProps, TableActionStates} from "@/PageComponents/Table/table-model";
import ScTable from "@/PageComponents/Table/ScTable";
import {Employee} from "@/interfaces/api/models";
import InviteEmployeesModal from "@/components/modals/employee/invite-employees";
import ConfirmAction from "@/components/modals/confirm-action";

// GetEmployees returns the invite columns alongside the employee record.
type EmployeeRow = Employee & {
    InviteStatus?: null | 'none' | 'pending' | 'expired' | 'accepted' | 'revoked'
    InviteExpiresAt?: string | null
}

// Rows in these states still have an invite link that can be sent again.
const resendableStatuses = ['pending', 'expired', 'revoked']
// Revoking only means something while the link is still live.
const revocableStatuses = ['pending', 'expired']

const inviteMarkers: Record<string, { label: string; colour: string; icon: any }> = {
    pending: { label: 'Invite pending', colour: 'var(--mantine-color-orange-6)', icon: IconClock },
    expired: { label: 'Invite expired', colour: 'var(--mantine-color-red-6)', icon: IconAlertCircle },
    revoked: { label: 'Invite revoked', colour: 'var(--mantine-color-red-6)', icon: IconCircleX }
}

const inviteMarker = (status?: EmployeeRow['InviteStatus']) => {
    const marker = status ? inviteMarkers[status] : undefined
    if (!marker) return null

    const Icon = marker.icon
    return (
        <Tooltip label={marker.label} withArrow position={'right'} radius={'sm'} withinPortal>
            <Icon size={16} color={marker.colour} aria-label={marker.label} style={{ flex: 'none' }} />
        </Tooltip>
    )
}

// The 429 body carries RetryAfterSeconds, which Fetch leaves in the raw serverMessage text.
const readRetryAfterSeconds = (result: any) => {
    if (typeof result?.RetryAfterSeconds === 'number') return result.RetryAfterSeconds

    const parsed = Helper.parseString(result?.serverMessage)
    return typeof parsed?.RetryAfterSeconds === 'number' ? parsed.RetryAfterSeconds : null
}

const isThrottled = (result: any) => result?.status === 429 || result?.HttpStatusCode === 429

const formatWait = (seconds: number) => {
    if (seconds < 60) return `${seconds} seconds`

    const minutes = Math.ceil(seconds / 60)
    return minutes === 1 ? '1 minute' : `${minutes} minutes`
}

interface ResendResult {
    name: string
    link?: string
    whatsAppUrl?: string
}

const SettingsEmployeeTable: FC = () => {

    const employeeTableProps = useMemo<ScTableProps>(() => (
        {
            columnMappingModelName: 'EmployeeList',
            columMappingOverrideValues: {
                'FullName': {
                    // Label: 'Name',
                    Width: 220,
                    CellType: 'employee',
                    MetaData: JSON.stringify({
                        displayColorKeyName: 'DisplayColor',
                        href: '/settings/employee/',
                        slug: 'ID'
                    }),
                    DisplayValueFunction: (x: EmployeeRow) => <Flex align={'center'} gap={5} wrap={'nowrap'}><span style={{ whiteSpace: 'nowrap' }}>{x.FullName}</span>{x.Owner && <Tooltip label="Owner" withArrow position="right" color={'goldenrod'} radius="sm" withinPortal><IconUserStar size={16} color={'var(--mantine-color-scBlue-9)'} style={{ flex: 'none' }} /></Tooltip>}{inviteMarker(x.InviteStatus)}</Flex>,
                },
                'UserName': {
                    CellType: 'link',
                    MetaData: JSON.stringify({
                        href: '/settings/employee/',
                        slug: 'ID'
                    }),
                },
                /*'EmailAddress': {
                    // CellType: 'employee',
                },
                'MobileNumber': {
                    CellType: 'none',
                },*/
                'SendEmail': {
                    CellType: 'icon',
                },
                'SendSMS': {
                    CellType: 'icon',
                },
                /*'ModifiedBy': {
                    CellType: "none",
                },
                'ModifiedDate': {
                    CellType: "date",
                },*/
                'AuthUserIsActive': {
                    CellType: 'icon',
                }
            },
            tableDataEndpoint: '/Employee/GetEmployees',
            defaultSort: { SortExpression: 'CreatedDate', SortDirection: 'descending' },
            tableName: 'employees',
            tableNoun: 'Employee',
            tableFilterMetaData: {
                options: [
                    {
                        filterName: 'IncludeDisabled',
                        label: 'Include disabled employees',
                        type: 'switch',
                    }
                ]
            },
            actions: [
                {
                    type: 'default',
                    name: 'open',
                    icon: <IconEdit />,
                    label: 'Edit',
                    default: true
                },
                {
                    type: 'default',
                    name: 'resend',
                    icon: <IconSend />,
                    label: 'Resend invite',
                    activeLabel: 'Resending invite',
                    buttonProps: { 'aria-label': 'Resend invite' },
                    showFunction: (item: EmployeeRow) => resendableStatuses.includes(item.InviteStatus ?? '')
                },
                {
                    type: 'warning',
                    name: 'revoke',
                    icon: <IconUserOff />,
                    label: 'Revoke invite',
                    activeLabel: 'Revoking invite',
                    buttonProps: { 'aria-label': 'Revoke invite' },
                    showFunction: (item: EmployeeRow) => revocableStatuses.includes(item.InviteStatus ?? '')
                }
            ],
            bottomRightSpace: true,
            selectMode: 'single',
        }
    ), [])

    const router = useRouter()

    const subscriptionInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo);
    const isTenantInTrial = subscriptionInfo?.AccessStatus === Enums.AccessStatus.Trial;
    const addEmployeeDisabled = PS.isMasterSystemAdmin() && !isTenantInTrial;

    const [actionStates, setActionStates] =
        useState<TableActionStates>({});

    const [showInviteModal, setShowInviteModal] = useState(false);
    const [refreshToggle, setRefreshToggle] = useState(false);
    const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());
    const [resendResult, setResendResult] = useState<ResendResult | null>(null);

    const refreshTable = useCallback(() => setRefreshToggle((p: boolean) => !p), [])

    const setActionState = useCallback((name: string, item: any, state: 'loading' | 'none') => {
        setActionStates((p: TableActionStates) => ({
            ...p,
            [name + item.ID]: state
        }))
    }, [])

    const resendInvite = useCallback(async (item: EmployeeRow) => {
        setActionState('resend', item, 'loading')

        let result: any
        try {
            result = await Fetch.post({ url: '/Employee/ResendInvite/' + item.ID })
        } catch (err: any) {
            // Fetch normally returns its errors, but a network failure still throws.
            result = err
        }

        setActionState('resend', item, 'none')

        if (isThrottled(result)) {
            const retryAfterSeconds = readRetryAfterSeconds(result)
            showNotification({
                color: 'yellow.7',
                message: `Please wait ${formatWait(retryAfterSeconds ?? 60)} before resending.`,
                autoClose: 5000
            })
            return
        }

        if (result?.InviteLink || result?.InviteWhatsAppUrl) {
            setResendResult({
                name: item.FullName || item.EmailAddress || 'Employee',
                link: result.InviteLink,
                whatsAppUrl: result.InviteWhatsAppUrl
            })
            refreshTable()
            return
        }

        showNotification({
            color: 'yellow.7',
            message: result?.Message || result?.message || 'Could not resend the invite.',
            autoClose: 5000
        })
    }, [])

    const revokeInvite = useCallback(async (item: EmployeeRow) => {
        setActionState('revoke', item, 'loading')

        let result: any
        try {
            result = await Fetch.post({ url: '/Employee/RevokeInvite/' + item.ID })
        } catch (err: any) {
            result = err
        }

        setActionState('revoke', item, 'none')

        if (result?.Revoked) {
            showNotification({ color: 'green.7', message: 'Invite revoked', autoClose: 3000 })
            refreshTable()
            return
        }

        showNotification({
            color: 'yellow.7',
            message: result?.Message || result?.message || 'Could not revoke the invite.',
            autoClose: 5000
        })
    }, [])

    const onAction = useCallback((name: string, item: any) => {
        if (name === 'open') {
            router.replace('/settings/employee/' + item.ID)
        } else if (name === 'resend') {
            resendInvite(item)
        } else if (name === 'revoke') {
            setConfirmOptions({
                ...Helper.initialiseConfirmOptions(),
                display: true,
                heading: 'Revoke invite',
                text: 'Revoke this invite? The link will stop working and their login access will be turned off.',
                confirmButtonText: 'Revoke invite',
                onConfirm: () => revokeInvite(item)
            })
        }
    }, [])

    const inviteTooltipLabel = addEmployeeDisabled ? "ServCraft Admin cannot add employees" : "";

    const noItemsActionButton = (
        <Tooltip label={inviteTooltipLabel} disabled={!addEmployeeDisabled}>
            <Button color={'scBlue'} size={'md'} rightSection={<IconUserPlus size={16} />} disabled={addEmployeeDisabled} onClick={() => setShowInviteModal(true)}>
                Invite employees
            </Button>
        </Tooltip>
    )

    return (
        <>
            <ScTable
                {...employeeTableProps}
                actionStates={actionStates}
                onAction={onAction}
                forceDataRefreshFlipFlop={refreshToggle}
                noItemsAction={noItemsActionButton}
                noItemsSecondaryMessage={'Invite your team so they can sign in and start working on jobs.'}
            >
                <Group gap={'xs'}>
                    <Link
                        href={'/settings/employee/create'}
                        onClick={() => Helper.nextLinkClicked('/settings/employee/create')}
                    >
                        <Tooltip label={inviteTooltipLabel} disabled={!addEmployeeDisabled}>
                            <Button variant={'outline'} color={'scBlue'} rightSection={<IconPlus size={14} />} disabled={addEmployeeDisabled}>
                                Add Employee
                            </Button>
                        </Tooltip>
                    </Link>

                    <Tooltip label={inviteTooltipLabel} disabled={!addEmployeeDisabled}>
                        <Button color={'scBlue'} rightSection={<IconUserPlus size={16} />} disabled={addEmployeeDisabled} onClick={() => setShowInviteModal(true)}>
                            Invite employees
                        </Button>
                    </Tooltip>
                </Group>
            </ScTable>

            <InviteEmployeesModal
                open={showInviteModal}
                onClose={() => setShowInviteModal(false)}
                onInvited={refreshTable}
            />

            <Modal
                opened={!!resendResult}
                onClose={() => setResendResult(null)}
                title={'Invite resent'}
                centered
            >
                <Text size={'sm'} c={'gray.7'} mb={'md'}>
                    We emailed and SMS'd a new join link to {resendResult?.name}.
                </Text>
                <Group gap={'xs'}>
                    <Button
                        color={'scBlue'}
                        leftSection={<IconCopy size={14} />}
                        disabled={!resendResult?.link}
                        onClick={() => {
                            Helper.copyToClipboard(resendResult?.link)
                            showNotification({ color: 'green.7', message: 'Copied', autoClose: 2000 })
                        }}
                    >
                        Copy link
                    </Button>
                    <Button
                        variant={'outline'}
                        color={'gray.7'}
                        leftSection={<IconBrandWhatsapp size={14} />}
                        component={'a'}
                        href={resendResult?.whatsAppUrl}
                        target={'_blank'}
                        rel={'noreferrer'}
                        disabled={!resendResult?.whatsAppUrl}
                    >
                        Share on WhatsApp
                    </Button>
                </Group>
                <Text size={'sm'} c={'dimmed'} mt={'md'}>Link expires in 7 days and can only be used once.</Text>
            </Modal>

            <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions} />
        </>
    )
}

export default SettingsEmployeeTable
